import React, { useState, useEffect, useCallback } from 'react';
import { statusMeta, inr, STATUS_OPTIONS, JOURNEY_STAGES } from './constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLUMN_TINT = {
  new: 'border-t-blue-400', contacted: 'border-t-yellow-400', nurturing: 'border-t-purple-400',
  demo_scheduled: 'border-t-orange-400', demo_done: 'border-t-indigo-400',
  conversion_journey: 'border-t-teal-500',
  negotiating: 'border-t-pink-400', closed_won: 'border-t-green-500', closed_lost: 'border-t-red-400',
};

export default function KanbanBoard({ token, onOpenLead, refreshKey }) {
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchBoard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/crm/board`, { headers });
      const d = await res.json();
      if (d.success) setColumns(d.columns || []);
    } catch (e) { console.error('board', e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchBoard(); }, [fetchBoard, refreshKey]);

  const moveLead = async (leadId, newStage, fromStage) => {
    if (newStage === fromStage) return;
    // optimistic
    setColumns(cols => cols.map(c => {
      if (c.stage === fromStage) {
        const lead = c.leads.find(l => l.id === leadId);
        return { ...c, count: Math.max(0, c.count - 1), leads: c.leads.filter(l => l.id !== leadId), deal_sum: c.deal_sum - (lead?.deal_value || 0) };
      }
      return c;
    }));
    try {
      await fetch(`${API_URL}/api/crm/leads/bulk-status`, {
        method: 'POST', headers, body: JSON.stringify({ ids: [leadId], status: newStage }) });
    } catch (e) { console.error('move', e); }
    fetchBoard();
  };

  if (loading) {
    return <div className="py-12 text-center"><div className="inline-block animate-spin rounded-full h-7 w-7 border-b-2 border-slate-500" /></div>;
  }

  const JourneyDots = ({ steps }) => (
    <div className="flex items-center gap-1 mt-2" data-testid="journey-dots">
      {JOURNEY_STAGES.map((s, i) => (
        <div key={s.key} title={s.label}
          className={`h-1.5 flex-1 rounded-full ${steps && steps[s.key] ? 'bg-teal-500' : 'bg-slate-200'}`} />
      ))}
    </div>
  );

  const LeadCard = ({ l, fromStage }) => (
    <div draggable
      onDragStart={() => setDragId({ id: l.id, from: fromStage })}
      onClick={() => onOpenLead(l.id)}
      data-testid={`kanban-card-${l.id}`}
      className="bg-white rounded-lg p-2.5 border border-slate-200 cursor-grab active:cursor-grabbing hover:shadow-md transition">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-800 truncate">{l.name || 'Lead'}</p>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full text-white font-bold ${l.temperature === 'hot' ? 'bg-red-500' : l.temperature === 'warm' ? 'bg-orange-400' : 'bg-slate-400'}`}>{l.lead_score}</span>
      </div>
      {l.school_name && <p className="text-[11px] text-slate-400 truncate">🏫 {l.school_name}</p>}
      <p className="text-[11px] text-slate-500 mt-1">{l.phone} · <span className="text-emerald-600">{inr(l.deal_value)}</span></p>
      {l.cj_stuck && (
        <span data-testid="cj-stuck-badge" className="inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
          ⚠️ {l.cj_stuck_days || 3}+ दिन से अटका
        </span>
      )}
      {l.journey_steps && <JourneyDots steps={l.journey_steps} />}
      <select value={fromStage} onClick={(e) => e.stopPropagation()}
        onChange={(e) => { e.stopPropagation(); moveLead(l.id, e.target.value, fromStage); }}
        data-testid={`kanban-status-${l.id}`}
        className="mt-2 w-full text-[11px] px-1.5 py-1 rounded-md border border-slate-200 bg-slate-50 cursor-pointer">
        {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  return (
    <div className="overflow-x-auto pb-2" data-testid="crm-kanban-board">
      <div className="flex gap-3 min-w-max">
        {columns.map((col) => (
          <div key={col.stage}
            onDragOver={(e) => { e.preventDefault(); setDragOver(col.stage); }}
            onDragLeave={() => setDragOver(null)}
            onDrop={(e) => { e.preventDefault(); if (dragId) moveLead(dragId.id, col.stage, dragId.from); setDragOver(null); setDragId(null); }}
            data-testid={`kanban-col-${col.stage}`}
            className={`${col.stage === 'conversion_journey' ? 'w-72' : 'w-64'} shrink-0 bg-slate-100 rounded-xl border-t-4 ${COLUMN_TINT[col.stage] || 'border-t-slate-300'} ${dragOver === col.stage ? 'ring-2 ring-slate-400' : ''}`}>
            <div className="px-3 py-2 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-700">{statusMeta(col.stage).label}</span>
                <span className="text-[11px] bg-white px-2 py-0.5 rounded-full text-slate-500">{col.count}</span>
              </div>
              <p className="text-[11px] text-emerald-600 font-medium mt-0.5">{inr(col.deal_sum)}</p>
            </div>

            {col.stage === 'conversion_journey' ? (
              <div className="p-2 space-y-3 min-h-[120px] max-h-[60vh] overflow-y-auto" data-testid="conversion-journey-stages">
                {(col.journey_stages || []).map((s) => {
                  const stageLeads = (col.leads || []).filter((l) => l.journey_step_key === s.key);
                  return (
                    <div key={s.key} data-testid={`cj-substage-${s.key}`}>
                      <div className="flex items-center justify-between px-1 mb-1">
                        <span className="text-[11px] font-bold text-teal-700">{s.label}</span>
                        <span className="text-[10px] bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded-full font-semibold">{(col.journey_counts || {})[s.key] || 0}</span>
                      </div>
                      <div className="space-y-2 pl-1 border-l-2 border-teal-100">
                        {stageLeads.length === 0
                          ? <p className="text-[10px] text-slate-300 py-1 pl-1">—</p>
                          : stageLeads.map((l) => <LeadCard key={l.id} l={l} fromStage={col.stage} />)}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-2 space-y-2 min-h-[120px] max-h-[60vh] overflow-y-auto">
                {col.leads.length === 0
                  ? <p className="text-[11px] text-slate-400 text-center py-4">—</p>
                  : col.leads.map((l) => <LeadCard key={l.id} l={l} fromStage={col.stage} />)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
